/**
 * Google Ads Conversions Block
 *
 * First new tracking block built on top of the Vibesite event bus —
 * validates the architecture end-to-end. Subscribes to canonical events
 * and fires `gtag('event', 'conversion', { send_to: 'AW-XXX/LABEL' })`
 * for each event the site has mapped to a conversion label.
 *
 * Configuration:
 *   conversion_id         — Google Ads conversion ID (e.g. "AW-1234567890")
 *   conversion_labels     — Slot group with one text field per canonical
 *                           event. The label is the second half of the
 *                           Google Ads `send_to` value. Empty = don't fire
 *                           a conversion for that event.
 *   default_value         — Optional numeric value attached to each
 *                           conversion (USD by default; override currency
 *                           via the `currency` field).
 *   currency              — ISO currency code (default 'USD').
 *
 * Multi-instance safe (corporate + property scopes can each install their
 * own block with different conversion IDs).
 */

import { useEffect, useRef, useMemo } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockToken,
  useBlockSlotGroup,
  useVibesiteEvents,
} from '@vibesites/runtime/react';
import {
  Event,
  VIBESITE_EVENTS,
  type VibesiteEventName,
} from '@vibesites/runtime';
import { useTrackingAllowed } from '../../shared/analytics';

// gtag.js loader — same library GA4 uses; loading once per page is safe.
const GTAG_SCRIPT_ID = 'gtag-js-loader';

function ensureGtagLoaded(conversionId: string) {
  const w = window as unknown as {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __gads_configured_ids?: Set<string>;
  };

  // Bootstrap dataLayer + gtag shim if no other block has done it yet.
  if (typeof w.gtag !== 'function') {
    if (!document.getElementById(GTAG_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = GTAG_SCRIPT_ID;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}`;
      script.async = true;
      document.head.appendChild(script);

      const inline = document.createElement('script');
      inline.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=window.gtag||gtag;gtag('js',new Date());`;
      document.head.appendChild(inline);
    }
  }

  // Configure this conversion ID once per page.
  const configured = (w.__gads_configured_ids ||= new Set<string>());
  if (!configured.has(conversionId) && typeof w.gtag === 'function') {
    w.gtag('config', conversionId);
    configured.add(conversionId);
  }
}

// Build the slot-group field schema dynamically from the catalog so adding a
// new canonical event automatically surfaces a label slot for it. Implicit
// events (phone/email) are included so sites can opt those into Ads
// conversions too if they want.
const LABEL_FIELDS = (Object.keys(Event) as VibesiteEventName[]).reduce(
  (acc, name) => {
    const meta = VIBESITE_EVENTS[name];
    acc[name] = {
      label: meta.label,
      type: 'text' as const,
      defaultValue: '',
      description: `Google Ads conversion label for "${name}". Leave blank to skip this event.`,
    };
    return acc;
  },
  {} as Record<string, {
    label: string;
    type: 'text';
    defaultValue: string;
    description: string;
  }>,
);

function GoogleAdsConversionsComponent(_props: BlockProps) {
  const conversionId = useBlockToken('conversion_id', { defaultValue: '' });
  const defaultValueToken = useBlockToken('default_value', { defaultValue: '' });
  const currency = useBlockToken('currency', { defaultValue: 'USD' });

  const labels = useBlockSlotGroup('conversion_labels', {
    label: 'Conversion Labels',
    description:
      'Map each canonical Vibesite event to its Google Ads conversion label.',
    fields: LABEL_FIELDS,
  });

  const { subscribe } = useVibesiteEvents();
  const trackingAllowed = useTrackingAllowed('advertising');
  const mountedRef = useRef(true);

  // Default value parsed once per change.
  const defaultValueNum = useMemo(() => {
    const n = parseFloat(defaultValueToken || '');
    return Number.isFinite(n) ? n : null;
  }, [defaultValueToken]);

  // Load gtag + configure this conversion ID.
  useEffect(() => {
    mountedRef.current = true;
    if (!trackingAllowed || !conversionId) return;
    ensureGtagLoaded(conversionId);
    return () => {
      mountedRef.current = false;
    };
  }, [trackingAllowed, conversionId]);

  // Subscribe to canonical events and fire conversions.
  useEffect(() => {
    if (!trackingAllowed || !conversionId) return;

    const off = subscribe('*', (name) => {
      const label = labels[name];
      if (!label) return; // Site hasn't mapped this event to a conversion.

      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (typeof w.gtag !== 'function') return;

      const eventParams: Record<string, unknown> = {
        send_to: `${conversionId}/${label}`,
      };
      if (defaultValueNum !== null) {
        eventParams.value = defaultValueNum;
        eventParams.currency = currency || 'USD';
      }

      w.gtag('event', 'conversion', eventParams);
    });

    return off;
  }, [trackingAllowed, conversionId, labels, defaultValueNum, currency, subscribe]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'GoogleAdsConversions',
  displayName: 'Google Ads Conversions',
  tokens: {
    conversion_id: {
      label: 'Conversion ID',
      type: 'text',
      description: 'Google Ads conversion ID (e.g. "AW-1234567890").',
      required: true,
    },
    default_value: {
      label: 'Default Conversion Value',
      type: 'text',
      defaultValue: '',
      description:
        'Optional numeric value attached to every conversion. Leave blank to omit value/currency from the gtag call.',
    },
    currency: {
      label: 'Currency Code',
      type: 'text',
      defaultValue: 'USD',
      description: 'ISO currency code attached to the conversion value (default USD).',
    },
  },
  slotGroups: {
    conversion_labels: {
      label: 'Conversion Labels',
      description:
        'Map each canonical Vibesite event to its Google Ads conversion label. Empty fields are skipped.',
      fields: LABEL_FIELDS,
    },
  },
};

export const googleAdsConversionsBlock: BlockDefinition = {
  id: 'google-ads-conversions',
  name: 'Google Ads Conversions',
  description:
    'Fires Google Ads conversion events for canonical Vibesite actions. Configure per-event conversion labels in the Conversion Labels slot group.',
  category: 'Analytics',
  placement: 'injection',
  defaultScope: 'global',
  component: GoogleAdsConversionsComponent,
  contentSchema,
  tags: ['google', 'ads', 'adwords', 'conversion', 'tracking', 'analytics'],
  trackedEvents: (Object.keys(Event) as VibesiteEventName[]).map((name) => ({
    event: name,
    mapsTo: `gtag('event', 'conversion', { send_to: 'AW-…/<label>' })`,
    description:
      'Fires only when the matching Conversion Label is populated for this event.',
  })),
};
